import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertCircle, Send, Eye, Languages } from 'lucide-react';
import { toast } from 'react-toastify';
import surveyApi from './surveyApi';
import { isAnswerable, answerIsEmpty, blankAnswer } from './questionTypes';
import SurveyQuestionInput from './components/SurveyQuestionInput';
import SurveyThankYou from './components/SurveyThankYou';
import QuestionImage from './components/QuestionImage';
import { useTrainingLang } from '../i18n/TrainingLangContext';

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export default function SurveyViewer() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Admin-only read-only view: renders the whole survey regardless of whether the
  // viewer has already responded, without creating a response or allowing submit.
  const preview = searchParams.get('preview') === '1';
  const { language, setLanguage, t, nativeLabelFor } = useTrainingLang();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stage, setStage] = useState('intro'); // intro | survey | done
  const [responseId, setResponseId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [invalid, setInvalid] = useState({}); // questionId -> true
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const startTimeRef = useRef(null);
  const topRef = useRef(null);

  // Re-fetched when the language changes. Question/option ids are the same in every
  // language, so answers already given survive a switch mid-survey.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const d = await surveyApi.getSurvey(surveyId, language);
        if (active) setData(d);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load survey');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [surveyId, language]);

  // Shuffle once per survey, not per language, so a switch doesn't reorder the page.
  const shuffledIds = useRef(null);
  const orderedQuestions = useMemo(() => {
    if (!data) return [];
    // Preview always uses the authoring order so the admin can verify edits in place.
    if (!data.survey.randomize_questions || preview) return data.questions;
    if (!shuffledIds.current) shuffledIds.current = shuffle(data.questions.map((q) => q.id));
    const byId = Object.fromEntries(data.questions.map((q) => [q.id, q]));
    return shuffledIds.current.map((id) => byId[id]).filter(Boolean);
  }, [data, preview]);

  const answerableQuestions = orderedQuestions.filter((q) => isAnswerable(q.question_type));
  const requiredQuestions = answerableQuestions.filter((q) => q.is_required);
  const answeredRequired = requiredQuestions.filter((q) => !answerIsEmpty(q.question_type, answers[q.id], q)).length;
  const progressPct = requiredQuestions.length
    ? Math.round((answeredRequired / requiredQuestions.length) * 100)
    : (answerableQuestions.filter((q) => !answerIsEmpty(q.question_type, answers[q.id], q)).length /
        Math.max(answerableQuestions.length, 1)) * 100;

  const alreadyResponded = data && data.my_response_count > 0 && !data.survey.allow_multiple_responses;

  const begin = async () => {
    try {
      setStarting(true);
      const { response_id } = await surveyApi.startResponse(surveyId, data.language);
      setResponseId(response_id);
      startTimeRef.current = Date.now();
      setStage('survey');
    } catch (e) {
      toast.error(e.message || t('surveyStartFailed'));
    } finally {
      setStarting(false);
    }
  };

  const setAnswer = (qId, next) => {
    setAnswers((prev) => ({ ...prev, [qId]: next }));
    setInvalid((prev) => (prev[qId] ? { ...prev, [qId]: false } : prev));
  };

  const submit = async () => {
    const bad = {};
    for (const q of requiredQuestions) {
      if (answerIsEmpty(q.question_type, answers[q.id], q)) bad[q.id] = true;
    }
    if (Object.keys(bad).length) {
      setInvalid(bad);
      toast.error(t('surveyAnswerRequired'));
      if (topRef.current) topRef.current.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    const payload = answerableQuestions
      .map((q) => {
        const a = answers[q.id];
        if (answerIsEmpty(q.question_type, a, q)) return null;
        return { question_id: q.id, option_ids: a.option_ids, value: a.value, text: a.text, ranks: a.ranks };
      })
      .filter(Boolean);
    const duration = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : null;
    try {
      setSubmitting(true);
      await surveyApi.submitResponse(responseId, payload, duration);
      setStage('done');
    } catch (e) {
      toast.error(e.message || t('surveySubmitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Only the first load blanks the page; a language switch swaps text in place.
  if (loading && !data) return <div className="p-8 text-slate-400">{t('loading')}</div>;
  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto p-4 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>
        <div className="max-w-2xl mx-auto mt-4">
          <BackLink navigate={navigate} label={t('surveyBack')} />
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { survey } = data;

  if (stage === 'done') {
    return <SurveyThankYou survey={survey} t={t} onExit={() => navigate('/tiles/ksp_training/surveys')} />;
  }

  // Intro screen (skipped in preview — go straight to the read-only body)
  if (stage === 'intro' && !preview) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <BackLink navigate={navigate} label={t('surveyBack')} />
        <div className="mt-4 rounded-lg bg-slate-800 border border-slate-700 p-6">
          {/* Asked up front: profile languages aren't reliable (plenty of "en"
              profiles answered in Spanish), so the respondent confirms it here. */}
          {(data.languages || []).length > 1 && (
            <div className="mb-5 pb-5 border-b border-slate-700">
              <p className="text-sm text-slate-300 mb-2 flex items-center gap-2">
                <Languages className="w-4 h-4 text-amber-400" /> {t('surveyLanguagePrompt')}
              </p>
              <div className="flex flex-wrap gap-2">
                {data.languages.map((code) => (
                  <button key={code} type="button" onClick={() => setLanguage(code)}
                    aria-pressed={data.language === code}
                    className={`px-4 py-2 rounded text-sm font-medium border transition-colors ${
                      data.language === code
                        ? 'bg-amber-600 border-amber-500 text-white'
                        : 'bg-slate-700/60 border-slate-600 text-slate-200 hover:bg-slate-600'
                    }`}>
                    {nativeLabelFor(code)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <h1 className="text-2xl font-bold text-slate-100">{survey.title}</h1>
          {survey.description && <p className="text-slate-300 mt-2">{survey.description}</p>}
          {survey.instructions_md && (
            <div className="mt-4 text-slate-300 whitespace-pre-wrap text-sm">{survey.instructions_md}</div>
          )}
          <div className="mt-4 flex items-center gap-4 text-sm text-slate-400">
            <span>{t('surveyQuestionCount', { count: data.questions.filter((q) => isAnswerable(q.question_type)).length })}</span>
            {survey.is_anonymous && (
              <span className="inline-flex items-center gap-1 text-emerald-300">
                <ShieldCheck className="w-4 h-4" /> {t('surveyAnonymous')}
              </span>
            )}
          </div>

          {alreadyResponded ? (
            <div className="mt-6 p-3 rounded bg-slate-700/40 border border-slate-600 text-slate-200 text-sm">
              {t('surveyAlreadyResponded')}
            </div>
          ) : (
            <button onClick={begin} disabled={starting}
              className="mt-6 px-5 py-2.5 rounded bg-amber-600 hover:bg-amber-500 text-white font-medium disabled:opacity-60">
              {starting ? t('starting') : t('surveyStart')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Survey body
  let qNumber = 0;
  return (
    <div className="max-w-2xl mx-auto p-6" ref={topRef}>
      <BackLink navigate={navigate} label={t('surveyBack')} />

      {preview && (
        <div className="mt-3 p-3 rounded bg-sky-900/30 border border-sky-700 text-sky-200 text-sm flex items-center gap-2">
          <Eye className="w-4 h-4 shrink-0" />
          Preview mode — this is a read-only view. Nothing you do here is saved, and no response is recorded.
        </div>
      )}

      <div className="mt-3 mb-5">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold text-slate-100">{survey.title}</h1>
          {survey.is_anonymous && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
              <ShieldCheck className="w-4 h-4" /> {t('surveyAnonymous')}
            </span>
          )}
        </div>
        {!preview && survey.show_progress !== 0 && survey.show_progress !== false && (
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div className="h-2 rounded-full bg-amber-500 transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        )}
      </div>

      <div className="space-y-4">
        {orderedQuestions.map((q) => {
          if (!isAnswerable(q.question_type)) {
            return (
              <div key={q.id} className="pt-2">
                <h2 className="text-lg font-semibold text-amber-300">{q.question_text}</h2>
                {q.help_text && <p className="text-sm text-slate-400 mt-1 whitespace-pre-wrap">{q.help_text}</p>}
                <QuestionImage surveyId={surveyId} question={q} />
              </div>
            );
          }
          qNumber += 1;
          const isInvalid = invalid[q.id];
          return (
            <div key={q.id}
              className={`bg-slate-800 border rounded-lg p-4 ${isInvalid ? 'border-rose-600' : 'border-slate-700'}`}>
              <div className="mb-3">
                <span className="text-slate-100 font-medium">
                  {qNumber}. {q.question_text}
                  {q.is_required && <span className="text-rose-400 ml-1">*</span>}
                </span>
                {q.help_text && <p className="text-sm text-slate-400 mt-1 whitespace-pre-wrap">{q.help_text}</p>}
              </div>
              <QuestionImage surveyId={surveyId} question={q} />
              <SurveyQuestionInput
                surveyId={surveyId}
                question={q}
                answer={answers[q.id] || blankAnswer()}
                onChange={(next) => setAnswer(q.id, next)}
                disabled={preview}
              />
              {isInvalid && (
                <p className="mt-2 text-xs text-rose-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> {t('surveyQuestionRequired')}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {preview ? (
        <div className="mt-6 text-sm text-slate-400 flex items-center gap-2 justify-end">
          <Eye className="w-4 h-4" /> Preview — submitting is disabled.
        </div>
      ) : (
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={submit} disabled={submitting}
            className="px-5 py-2.5 rounded bg-amber-600 hover:bg-amber-500 text-white font-medium flex items-center gap-2 disabled:opacity-60">
            <Send className="w-4 h-4" /> {submitting ? t('submitting') : t('surveySubmit')}
          </button>
        </div>
      )}
    </div>
  );
}

function BackLink({ navigate, label }) {
  return (
    <button onClick={() => navigate('/tiles/ksp_training/surveys')}
      className="text-sm text-slate-400 hover:text-slate-100 flex items-center gap-1">
      <ArrowLeft className="w-4 h-4" /> {label}
    </button>
  );
}
